/** 人事の画面で共通に使う問い合わせと小道具。 */
import { useQuery } from "@tanstack/react-query";
import { message } from "antd";
import { useCallback } from "react";

import type { Position } from "../../lib/api";
import { useApi, useMe } from "../../lib/useApi";
import { hasRole } from "../../lib/roles";

/** 書き込みの可否。画面のボタンの出し分けに使う(権限の真実は API 側)。 */
export function useHrPerms() {
  const me = useMe();
  return {
    canWrite: hasRole(me.data, "hr:operator"),
    canAdmin: hasRole(me.data, "hr:admin"),
  };
}

/** 組織マスタ(木の順・depth 付き)。所属の選択と組織図の根の選択に使う。 */
export function useOrgUnits(activeOnly = false) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "org-units", activeOnly],
    queryFn: () => api.orgUnits(activeOnly),
    staleTime: 30_000,
  });
}

export function usePositions(activeOnly = false) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "positions", activeOnly],
    queryFn: () => api.positions(activeOnly),
    staleTime: 30_000,
  });
}

/** カスタム項目の定義(フォーム・詳細・一覧の列を組む)。既定は稼働中だけ。 */
export function useCustomFields(activeOnly = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "custom-fields", activeOnly],
    queryFn: () => api.customFields(activeOnly),
    staleTime: 30_000,
  });
}

export type UnitOption = {
  value: number;
  title: string;
  children: UnitOption[];
};

/**
 * 平らな一覧から TreeSelect 用の木を組む(並びはサーバー側のまま)。
 *
 * 引数は `OrgUnit` そのものではなく必要な項目だけにしてある。動的グループの
 * `GET /groups/fields` が返す組織一覧(id / name / parent_id / active)も
 * 同じ木の組み方で出したいため。
 */
export function unitOptions(
  units: { id: number; name: string; parent_id: number | null; active: boolean }[],
): UnitOption[] {
  const nodes = new Map<number, UnitOption>();
  const roots: UnitOption[] = [];
  for (const u of units) {
    const node: UnitOption = {
      value: u.id,
      title: `${u.name}${u.active ? "" : "(停止中)"}`,
      children: [],
    };
    nodes.set(u.id, node);
    const parent = u.parent_id != null ? nodes.get(u.parent_id) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

export function positionOptions(positions: Position[]) {
  return positions.map((p) => ({ value: p.id, label: `${p.name}(${p.rank})` }));
}

/** 図(SVG)を別タブで開く。Authorization が要るので fetch して blob にする。 */
export function useOpenChartFile() {
  const api = useApi();
  return useCallback(
    async (path: string) => {
      try {
        const blob = await api.chartFile(path);
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener");
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        void message.error(e instanceof Error ? e.message : String(e));
      }
    },
    [api],
  );
}

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
